"use client"

import { useState, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import Chart from "chart.js/auto"
import "./KcCalculator.css"

const KcCalculator = () => {
  const { id, pid } = useParams()
  const cropId = id
  const projectId = pid
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()

  // Existing states
  const [crop, setCrop] = useState(null)
  const [kcData, setKcData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split("T")[0])
  const [kcValue, setKcValue] = useState(null)
  const [todayKcValue, setTodayKcValue] = useState(null)
  const [project, setProject] = useState(null)
  const [savedKcValues, setSavedKcValues] = useState([])
  const [autoCalculating, setAutoCalculating] = useState(false)

  // New states for project-based graph
  const [projectStatus, setProjectStatus] = useState("not_started")
  const [currentDayOfProject, setCurrentDayOfProject] = useState(0)
  const [projectKcData, setProjectKcData] = useState(null)
  const [projectProgress, setProjectProgress] = useState(0)

  // Chart refs
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const projectChartRef = useRef(null)
  const projectChartInstance = useRef(null)

  // Month names for recommendations
  const monthNames = [
    t("January"),
    t("February"),
    t("March"),
    t("April"),
    t("May"),
    t("June"),
    t("July"),
    t("August"),
    t("September"),
    t("October"),
    t("November"),
    t("December"),
  ]

  // Fetch crop and Kc curve data on component mount
  useEffect(() => {
    if (!cropId) return

    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch crop details
        const cropRes = await axios.get(`${API_URL}/api/crops/${cropId}`)
        setCrop(cropRes.data)

        // Fetch Kc curve data
        const kcRes = await axios.get(`${API_URL}/api/crops/calculate-kc-curve/${cropId}`)
        setKcData(kcRes.data)

        setLoading(false)
      } catch (err) {
        console.error("Error fetching data:", err)
        setError("Failed to fetch crop data")
        setLoading(false)
      }
    }

    fetchData()
  }, [cropId])

  // Get project data and determine status
  useEffect(() => {
    if (projectId) {
      fetchProjectData(projectId)
      fetchSavedKcValues(projectId)
    }
  }, [projectId])

  // Calculate project status and current day when project data is available
  useEffect(() => {
    if (project && crop && kcData) {
      calculateProjectStatus()
      calculateTodayKc()
    }
  }, [project, crop, kcData])

  // Create project-specific chart when project KC data is available
  useEffect(() => {
    if (projectKcData && projectChartRef.current) {
      createProjectChart()
    }
  }, [projectKcData, t])

  // Fetch project data
  const fetchProjectData = async (projId) => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${projId}`)
      setProject(response.data)
    } catch (err) {
      console.error("Error fetching project data:", err)
    }
  }

  // Calculate project status based on dates
  const calculateProjectStatus = () => {
    if (!project || !project.plantingDate) return

    const today = new Date()
    const plantingDate = new Date(project.plantingDate)
    const harvestDate = project.harvestDate ? new Date(project.harvestDate) : null

    // Calculate days since planting
    const daysSincePlanting = Math.floor((today - plantingDate) / (1000 * 60 * 60 * 24))

    if (today < plantingDate) {
      // Project not started yet
      setProjectStatus("not_started")
      setCurrentDayOfProject(0)
      setProjectProgress(0)
    } else if (harvestDate && today > harvestDate) {
      // Project completed
      setProjectStatus("completed")
      const totalProjectDays = Math.floor((harvestDate - plantingDate) / (1000 * 60 * 60 * 24))
      setCurrentDayOfProject(totalProjectDays)
      setProjectProgress(100)
      generateProjectKcData(totalProjectDays, true)
    } else {
      // Project is active
      setProjectStatus("active")
      setCurrentDayOfProject(daysSincePlanting)

      // Calculate progress based on total crop days
      const totalCropDays = crop.totalDays || 120
      const progress = Math.min((daysSincePlanting / totalCropDays) * 100, 100)
      setProjectProgress(progress)

      generateProjectKcData(daysSincePlanting, false)
    }
  }

  // Generate project-specific KC data
  const generateProjectKcData = (currentDay, isCompleted) => {
    if (!kcData) return

    const { dataPoints, stages, coefficients } = kcData
    const totalDays = isCompleted ? currentDay : Math.max(currentDay + 30, stages.total)

    // Create project-specific data points
    const projectDataPoints = []
    const actualDataPoints = []

    for (let day = 0; day <= totalDays; day++) {
      // Find the corresponding KC value from the original data
      const originalPoint =
        dataPoints.find((p) => p.day === day) ||
        dataPoints.reduce((prev, curr) => (Math.abs(curr.day - day) < Math.abs(prev.day - day) ? curr : prev))

      projectDataPoints.push({
        day: day,
        kc: originalPoint.kc,
        isActual: day <= currentDay,
        isProjected: day > currentDay && !isCompleted,
      })

      if (day <= currentDay) {
        actualDataPoints.push({
          day: day,
          kc: originalPoint.kc,
        })
      }
    }

    setProjectKcData({
      dataPoints: projectDataPoints,
      actualDataPoints: actualDataPoints,
      currentDay: currentDay,
      totalDays: totalDays,
      stages: stages,
      coefficients: coefficients,
      isCompleted: isCompleted,
    })

    // Save current KC value to database if project is active
    if (projectStatus === "active" && currentDay > 0) {
      saveCurrentKcToDatabase(currentDay, projectDataPoints[currentDay]?.kc)
    }
  }

  // Create the project-specific chart
  const createProjectChart = () => {
    if (!projectKcData || !projectChartRef.current) return

    // Destroy previous chart instance if it exists
    if (projectChartInstance.current) {
      projectChartInstance.current.destroy()
    }

    const ctx = projectChartRef.current.getContext("2d")
    const { dataPoints, actualDataPoints, currentDay, totalDays, stages, isCompleted } = projectKcData

    // Create datasets
    const datasets = []

    // Full KC curve (light gray for future days)
    datasets.push({
      label: t("Full KC Curve"),
      data: dataPoints.map((point) => ({
        x: point.day,
        y: point.kc,
      })),
      borderColor: "rgba(200, 200, 200, 0.5)",
      backgroundColor: "rgba(200, 200, 200, 0.1)",
      borderWidth: 1,
      fill: false,
      tension: 0.1,
    })

    // Actual KC curve (up to current day)
    if (actualDataPoints.length > 0) {
      datasets.push({
        label: t("Actual KC Progress"),
        data: actualDataPoints.map((point) => ({
          x: point.day,
          y: point.kc,
        })),
        borderColor: "#4caf50",
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        borderWidth: 3,
        fill: true,
        tension: 0.1,
      })
    }

    // Current day marker
    if (currentDay > 0) {
      const currentKc = dataPoints[currentDay]?.kc || 0
      datasets.push({
        label: t("Current Day"),
        data: [{ x: currentDay, y: currentKc }],
        backgroundColor: "#ff6b35",
        borderColor: "#ff6b35",
        pointRadius: 8,
        pointHoverRadius: 10,
        showLine: false,
      })
    }

    // Create the chart
    projectChartInstance.current = new Chart(ctx, {
      type: "line",
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: "linear",
            position: "bottom",
            title: {
              display: true,
              text: t("Days Since Planting"),
            },
            min: 0,
            max: totalDays,
            ticks: {
              stepSize: Math.max(10, Math.floor(totalDays / 10)),
            },
          },
          y: {
            title: {
              display: true,
              text: t("Crop Coefficient (Kc)"),
            },
            min: 0,
            max: 1.2,
            ticks: {
              stepSize: 0.1,
            },
          },
        },
        plugins: {
          title: {
            display: true,
            text: getProjectChartTitle(),
            font: {
              size: 16,
              weight: "bold",
            },
          },
          tooltip: {
            callbacks: {
              title: (tooltipItems) => {
                const day = tooltipItems[0].parsed.x
                return `${t("Day")}: ${day}`
              },
              label: (context) => {
                return `${t("Kc")}: ${context.parsed.y.toFixed(3)}`
              },
            },
          },
        },
      },
    })
  }

  // Get project chart title based on status
  const getProjectChartTitle = () => {
    switch (projectStatus) {
      case "not_started":
        return t("Project Not Started")
      case "active":
        return t("Project Progress - Day") + ` ${currentDayOfProject}`
      case "completed":
        return t("Project Completed")
      default:
        return t("Project KC Progress")
    }
  }

  // Save current KC value to database
  const saveCurrentKcToDatabase = async (day, kcValue) => {
    if (!projectId || !kcValue) return

    try {
      const today = new Date()
      const plantingDate = new Date(project.plantingDate)
      const currentDate = new Date(plantingDate)
      currentDate.setDate(currentDate.getDate() + day)

      // Check if this KC value is already saved for today
      const existingKc = savedKcValues.find((kc) => new Date(kc.date).toDateString() === currentDate.toDateString())

      if (!existingKc) {
        await axios.post(`${API_URL}/api/projects/${projectId}/kc-values`, {
          date: currentDate.toISOString().split("T")[0],
          kc: kcValue,
          stage: getCurrentGrowthStage(day),
          daysSincePlanting: day,
          cropId: cropId,
        })

        // Refresh saved KC values
        fetchSavedKcValues(projectId)
      }
    } catch (err) {
      console.error("Error saving KC value:", err)
    }
  }

  // Get current growth stage based on day
  const getCurrentGrowthStage = (day) => {
    if (!crop || !crop.growthStages) return "initial"

    const { initialDays, developmentDays, midSeasonDays } = crop.growthStages

    if (day <= initialDays) {
      return "initial"
    } else if (day <= initialDays + developmentDays) {
      return "development"
    } else if (day <= initialDays + developmentDays + midSeasonDays) {
      return "mid-season"
    } else {
      return "late-season"
    }
  }

  // Calculate today's KC value automatically
  const calculateTodayKc = async () => {
    try {
      const today = new Date().toISOString().split("T")[0]
      const res = await axios.post(`${API_URL}/api/crops/calculate-kc`, {
        cropId,
        projectId: projectId,
        date: today,
      })

      setTodayKcValue(res.data)

      // Highlight today's point on the main chart
      highlightPointOnChart(res.data.daysSincePlanting, res.data.kc, "today")
    } catch (err) {
      console.error("Error calculating today's Kc:", err)
    }
  }

  // Auto-save today's KC value to project
  // const autoSaveTodayKc = async (kcData) => {

  //   try {
  //     // Check if today's KC is already saved
  //     const today = new Date().toISOString().split("T")[0]
  //     const existingKc = savedKcValues.find((kc) => new Date(kc.date).toISOString().split("T")[0] === today)

  //     if (!existingKc) {
  //       await axios.post(`${API_URL}/api/projects/${projectId}/kc-values`, {
  //         date: today,
  //         kc: kcData.kc,
  //         stage: kcData.stage,
  //         daysSincePlanting: kcData.daysSincePlanting,
  //         cropId: cropId,
  //       })

  //       fetchSavedKcValues(projectId)
  //       showNotification(t("Today's KC value auto-saved"), "success")
  //     }
  //   } catch (err) {
  //     console.error("Error auto-saving today's KC:", err)
  //   }
  // }

  // Add function to fetch saved KC values
  const fetchSavedKcValues = async (projId) => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${projId}/kc-values`)
      setSavedKcValues(response.data)
    } catch (err) {
      console.error("Error fetching saved KC values:", err)
    }
  }

  // Add function to save KC value to project
  const calculateKcForDate = async () => {
    if (!cropId || !targetDate) return

    try {
      const res = await axios.post(`${API_URL}/api/crops/calculate-kc`, {
        cropId,
        projectId: projectId,
        date: targetDate,
      })

      setKcValue(res.data)
      highlightPointOnChart(res.data.daysSincePlanting, res.data.kc, "selected")
    } catch (err) {
      console.error("Error calculating Kc:", err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const handleDateChange = (e) => {
    setTargetDate(e.target.value)
  }

  const saveKcToProject = async () => {
    if (!projectId || !kcValue) {
      showNotification(t("No project or KC value to save"), "error")
      return
    }

    try {
      await axios.post(`${API_URL}/api/projects/${projectId}/kc-values`, {
        date: targetDate,
        kc: kcValue.kc,
        stage: kcValue.stage,
        daysSincePlanting: kcValue.daysSincePlanting,
        cropId: cropId,
      })

      showNotification(t("KC value saved to project"), "success")
      fetchSavedKcValues(projectId)
    } catch (err) {
      console.error("Error saving KC value:", err)
      showNotification(err.response?.data?.message || t("Failed to save KC value"), "error")
    }
  }

  const highlightPointOnChart = (day, kc, type = "selected") => {
    if (!chartInstance.current) return

    // Remove previous highlight points
    const datasets = chartInstance.current.data.datasets
    const highlightIndices = []

    datasets.forEach((ds, index) => {
      if (ds.label === "Selected Date" || ds.label === "Today's KC") {
        highlightIndices.push(index)
      }
    })

    highlightIndices.reverse().forEach((index) => {
      datasets.splice(index, 1)
    })

    // Add new highlight point
    const color = type === "today" ? "#ff6b35" : "red"
    const label = type === "today" ? "Today's KC" : "Selected Date"

    datasets.push({
      label: label,
      data: [{ x: day, y: kc }],
      backgroundColor: color,
      borderColor: color,
      pointRadius: 8,
      pointHoverRadius: 10,
      showLine: false,
    })

    chartInstance.current.update()
  }

  // Create or update main chart when kcData changes
  useEffect(() => {
    if (!kcData || !chartRef.current) return

    // Destroy previous chart instance if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    const ctx = chartRef.current.getContext("2d")
    const { dataPoints, stageBoundaries, stages, coefficients } = kcData

    // Create datasets
    const datasets = [
      {
        label: t("Kc Curve"),
        data: dataPoints.map((point) => ({
          x: point.day,
          y: point.kc,
        })),
        borderColor: "#4caf50",
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        borderWidth: 2,
        fill: true,
        tension: 0.1,
      },
    ]

    // Create stage boundary lines
    stageBoundaries.forEach((boundary, index) => {
      if (index > 0 && index < stageBoundaries.length - 1) {
        datasets.push({
          label: "",
          data: [
            { x: boundary.day, y: 0 },
            { x: boundary.day, y: 1.2 },
          ],
          borderColor: "rgba(0, 0, 0, 0.2)",
          borderWidth: 1,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
        })
      }
    })

    // Create the chart
    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: "linear",
            position: "bottom",
            title: {
              display: true,
              text: t("Growth Period (days)"),
            },
            min: 0,
            max: stages.total,
            ticks: {
              stepSize: Math.max(10, Math.floor(stages.total / 10)),
            },
          },
          y: {
            title: {
              display: true,
              text: t("Crop Coefficient (Kc)"),
            },
            min: 0,
            max: 1.2,
            ticks: {
              stepSize: 0.1,
            },
          },
        },
        plugins: {
          title: {
            display: true,
            text: t("General KC Curve"),
            font: {
              size: 16,
              weight: "bold",
            },
          },
          tooltip: {
            callbacks: {
              title: (tooltipItems) => {
                const day = tooltipItems[0].parsed.x
                let stage = ""

                if (day <= stages.initial) {
                  stage = t("Initial Stage")
                } else if (day <= stages.initial + stages.development) {
                  stage = t("Development Stage")
                } else if (day <= stages.initial + stages.development + stages.mid) {
                  stage = t("Mid-Season Stage")
                } else {
                  stage = t("Late Season Stage")
                }

                return `${t("Day")}: ${day} - ${stage}`
              },
              label: (context) => {
                return `${t("Kc")}: ${context.parsed.y.toFixed(3)}`
              },
            },
          },
        },
      },
    })

    // Re-highlight today's KC if it exists
    if (todayKcValue) {
      highlightPointOnChart(todayKcValue.daysSincePlanting, todayKcValue.kc, "today")
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }
    }
  }, [kcData, t])

  const removeKcValue = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}/kc-values/${id}`)
      showNotification(t("KC value removed from project"), "success")
      fetchSavedKcValues(projectId)
    } catch (err) {
      console.error("Error removing KC value:", err)
      showNotification(err.response?.data?.message || t("Failed to remove KC value"), "error")
    }
  }

  if (loading) {
    return (
      <div className="kc-calculator-container" dir={direction}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t("Loading crop data...")}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="kc-calculator-container" dir={direction}>
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {t(error)}
        </div>
      </div>
    )
  }

  if (!crop || !kcData) {
    return (
      <div className="kc-calculator-container" dir={direction}>
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {t("Crop data not found")}
        </div>
      </div>
    )
  }

  return (
    <div className="kc-calculator-container" dir={direction}>
      <div className="kc-calculator-header">
        <h1>{t("Crop Coefficient (Kc) Calculator")}</h1>
        <p className="subtitle">
          {/* Crop Type Name: {crop.cropTypeDetails?.name || "Not Available"} |{" "} */}
          Crop Name: {crop.name || "Not Available"} |{" "}
          Region: {crop.region || "Not Available"} |{" "}
          Plant Date: {crop.plantMonth ? `1st ${monthNames[crop.plantMonth - 1]}` : "Not Available"}
        </p>

        {project && (
          <div className="project-info">
            <span className={`project-status ${project.status}`}>
              {t("Project Status")}: {t(project.status)}
            </span>
            {project.plantingDate && (
              <span className="planting-date">
                {t("Planting Date")}: {new Date(project.plantingDate).toLocaleDateString()}
              </span>
            )}
            {project.harvestDate && (
              <span className="harvest-date">
                {t("Harvest Date")}: {new Date(project.harvestDate).toLocaleDateString()}
              </span>
            )}
            {projectStatus === "active" && (
              <span className="project-progress">
                {t("Progress")}: {projectProgress.toFixed(1)}% ({currentDayOfProject} {t("days")})
              </span>
            )}
          </div>
        )}
      </div>

      {/* Today's KC Status Card */}
      {todayKcValue && projectStatus === "active" && (
        <div className="today-kc-card">
          <div className="card-header">
            <h2>{t("Today's KC Status")}</h2>
            <span className="today-date">{new Date().toLocaleDateString()}</span>
          </div>
          <div className="card-body">
            <div className="today-kc-grid">
              <div className="today-kc-item highlight">
                <span className="label">{t("Current KC")}:</span>
                <span className="value">{todayKcValue.kc}</span>
              </div>
              <div className="today-kc-item">
                <span className="label">{t("Growth Stage")}:</span>
                <span className="value">{t(todayKcValue.stage)}</span>
              </div>
              <div className="today-kc-item">
                <span className="label">{t("Days Since Planting")}:</span>
                <span className="value">
                  {todayKcValue.daysSincePlanting} {t("days")}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="kc-calculator-grid">
        <div className="crop-info-section">
          <div className="card">
            <div className="card-header">
              <h2>{t("Crop Information")}</h2>
            </div>
            <div className="card-body">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">{t("Crop Type")}:</span>
                  <span className="info-value">{crop.cropTypeDetails?.name || crop.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t("Crop")}:</span>
                  <span className="info-value">{crop.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t("Region")}:</span>
                  <span className="info-value">{crop.region || "Not Available"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t("Plant Month")}:</span>
                  <span className="info-value">{monthNames[crop.plantMonth - 1]}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t("Total Growth Period")}:</span>
                  <span className="info-value">
                    {crop.totalDays} {t("days")}
                  </span>
                </div>
              </div>

              <div className="growth-stages">
                <h3>{t("Growth Stages")}</h3>
                <div className="stages-grid">
                  <div className="stage-item">
                    <span className="stage-label">{t("Initial")}:</span>
                    <span className="stage-value">
                      {crop.growthStages.initialDays} {t("days")}
                    </span>
                  </div>
                  <div className="stage-item">
                    <span className="stage-label">{t("Development")}:</span>
                    <span className="stage-value">
                      {crop.growthStages.developmentDays} {t("days")}
                    </span>
                  </div>
                  <div className="stage-item">
                    <span className="stage-label">{t("Mid-Season")}:</span>
                    <span className="stage-value">
                      {crop.growthStages.midSeasonDays} {t("days")}
                    </span>
                  </div>
                  <div className="stage-item">
                    <span className="stage-label">{t("Late Season")}:</span>
                    <span className="stage-value">
                      {crop.growthStages.lateSeasonDays} {t("days")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="crop-coefficients">
                <h3>{t("Crop Coefficients")}</h3>
                <div className="coefficients-grid">
                  <div className="coefficient-item">
                    <span className="coefficient-label">{t("Kc ini")}:</span>
                    <span className="coefficient-value">{crop.cropCoefficients.initial}</span>
                  </div>
                  <div className="coefficient-item">
                    <span className="coefficient-label">{t("Kc mid")}:</span>
                    <span className="coefficient-value">{crop.cropCoefficients.mid}</span>
                  </div>
                  <div className="coefficient-item">
                    <span className="coefficient-label">{t("Kc end")}:</span>
                    <span className="coefficient-value">{crop.cropCoefficients.end}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2>{t("Calculate Kc for Date")}</h2>
            </div>
            <div className="card-body">
              <div className="date-selector">
                <label htmlFor="targetDate">{t("Select Date")}:</label>
                <input type="date" id="targetDate" value={targetDate} onChange={handleDateChange} />
                <button className="calculate-button" onClick={calculateKcForDate}>
                  {t("Calculate")}
                </button>
              </div>

              {kcValue && (
                <div className="kc-result">
                  <h3>{t("Calculation Result")}</h3>
                  <div className="result-grid">
                    <div className="result-item">
                      <span className="result-label">{t("Date")}:</span>
                      <span className="result-value">{new Date(kcValue.date).toLocaleDateString()}</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">{t("Days Since Planting")}:</span>
                      <span className="result-value">
                        {kcValue.daysSincePlanting} {t("days")}
                      </span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">{t("Growth Stage")}:</span>
                      <span className="result-value">{t(kcValue.stage)}</span>
                    </div>
                    <div className="result-item highlight">
                      <span className="result-label">{t("Kc Value")}:</span>
                      <span className="result-value">{kcValue.kc}</span>
                    </div>
                  </div>
                  {/* {projectId && (
                    <div className="save-actions">
                      <button className="save-button" onClick={saveKcToProject}>
                        {t("Save to Project")}
                      </button>
                    </div>
                  )} */}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="kc-chart-section">
          {/* General KC Curve Chart */}
          <div className="card">
            <div className="card-header">
              <h2>{t("General Kc Curve")}</h2>
            </div>
            <div className="card-body">
              <div className="chart-container">
                <canvas ref={chartRef}></canvas>
              </div>
            </div>
          </div>

          {/* Project-Specific KC Chart */}
          {projectId && (
            <div className="card project-chart-card">
              <div className="card-header">
                <h2>{t("Project KC Progress")}</h2>
                {projectStatus === "active" && (
                  <div className="progress-indicator">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${projectProgress}%` }}></div>
                    </div>
                    <span className="progress-text">{projectProgress.toFixed(1)}%</span>
                  </div>
                )}
              </div>
              <div className="card-body">
                {projectStatus === "not_started" ? (
                  <div className="project-not-started">
                    <i className="fas fa-calendar-times"></i>
                    <h3>{t("Project Not Started")}</h3>
                    <p>{t("The project planting date is in the future.")}</p>
                    <p>
                      {/*{t("Planting Date")}: {new Date(project.plantingDate).toLocaleDateString()}*/}
                    </p>
                  </div>
                ) : (
                  <div className="chart-container">
                    <canvas ref={projectChartRef}></canvas>
                  </div>
                )}

                {projectStatus === "completed" && (
                  <div className="project-completed-info">
                    <i className="fas fa-check-circle"></i>
                    <p>{t("Project completed successfully!")}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {projectId && savedKcValues.length > 0 && (
        <div className="saved-kc-values">
          <h3>{t("Saved Kplant Values")}</h3>
          <div className="saved-values-table">
            <table>
              <thead>
                <tr>
                  <th>{t("Date")}</th>
                  <th>{t("KC Value")}</th>
                  <th>{t("Growth Stage")}</th>
                  <th>{t("Days Since Planting")}</th>
                  {/* <th>{t("Actions")}</th> */}
                </tr>
              </thead>
              <tbody>
                {savedKcValues.map((value, index) => (
                  <tr key={index}>
                    <td>{new Date(value.date).toLocaleDateString()}</td>
                    <td>{value.kc}</td>
                    <td>{t(value.stage)}</td>
                    <td>{value.daysSincePlanting}</td>
                    {/* <td>
                      <button className="remove-button" onClick={() => removeKcValue(value._id)}>
                        {t("Remove")}
                      </button>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default KcCalculator
